'use client'

import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react'

import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from 'react-simple-maps'
import { geoMercator, geoPath } from 'd3-geo'
import { Plus, Minus, X } from 'lucide-react'

import Button from '/react4xp/components/prototype/Button'

import type { LocationMapProps } from './types'

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json'

// Covers the populated world (Arctic Norway/Alaska down to Patagonia/Tasmania), not the literal
// poles — fitting the full globe forces so much vertical crop on wide screens that real office
// markers like Norway end up cropped above the frame.
const WORLD_LAND_BOUNDS = {
	type: 'MultiPoint' as const,
	coordinates: [
		[-180, -58],
		[180, 75],
	],
}

function getCoverScale(width: number, height: number) {
	const basis = geoMercator().fitSize([width, height], WORLD_LAND_BOUNDS)
	const [[x0, y0], [x1, y1]] = geoPath(basis).bounds(WORLD_LAND_BOUNDS)
	const coverFactor = Math.max(width / (x1 - x0), height / (y1 - y0))

	return basis.scale() * coverFactor
}

const INITIAL_CENTER: [number, number] = [0, geoMercator().fitSize([2, 2], WORLD_LAND_BOUNDS).invert!([1, 1])![1]]

const LABEL_HEIGHT = 20

export default function LocationMap({
	overline,
	title,
	intro,
	primaryCta,
	secondaryCta,
	locations = [],
	labels = {},
}: LocationMapProps) {
	const { zoomInLabel = 'Zoom in', zoomOutLabel = 'Zoom out', closeLabel = 'Close' } = labels

	const [zoom, setZoom] = useState(1.3)
	const [center, setCenter] = useState<[number, number]>(INITIAL_CENTER)
	const [activeCard, setActiveCard] = useState<string | null>(null)
	const [cardPos, setCardPos] = useState<{ x: number; y: number }>({ x: 10, y: 10 })
	const [mapSize, setMapSize] = useState({ width: 1234, height: 520 })
	const scale = useMemo(() => getCoverScale(mapSize.width, mapSize.height), [mapSize])
	const projection = useMemo(
		() => geoMercator().scale(scale).translate([mapSize.width / 2, mapSize.height / 2]),
		[scale, mapSize],
	)
	const centerY = projection(center)![1]
	const translateExtent = useMemo(() => geoPath(projection).bounds(WORLD_LAND_BOUNDS), [projection])

	const mapRef = useRef<HTMLDivElement>(null)
	const cardRef = useRef<HTMLDivElement | null>(null)

	useLayoutEffect(() => {
		const wrapper = mapRef.current
		if (!wrapper) return

		const initial = wrapper.getBoundingClientRect()
		if (initial.width > 0 && initial.height > 0) setMapSize({ width: initial.width, height: initial.height })

		const observer = new ResizeObserver(([entry]) => {
			const { width, height } = entry.contentRect
			if (width > 0 && height > 0) setMapSize({ width, height })
		})

		observer.observe(wrapper)
		return () => observer.disconnect()
	}, [])

	const handleZoomIn = () => setZoom((z) => Math.min(z * 1.5, 20))
	const handleZoomOut = () => setZoom((z) => Math.max(z / 1.5, 1))

	const isZoomMin = zoom <= 1
	const isZoomMax = zoom >= 20

	const handleMarkerClick = (e: React.MouseEvent<SVGGElement, MouseEvent>, id: string) => {
		const mapBounds = mapRef.current?.getBoundingClientRect()
		if (!mapBounds) return

		const cardWidth = 300
		const cardHeight = cardRef.current?.offsetHeight || 210
		const margin = 10

		let x = e.clientX - mapBounds.left + 10
		let y = e.clientY - mapBounds.top + 10

		setActiveCard(id)

		if (x + cardWidth > mapBounds.width) {
			x = mapBounds.width - cardWidth - margin
			if (x < margin) x = margin
		}

		if (y + cardHeight > mapBounds.height) {
			y = mapBounds.height - cardHeight - margin
			if (y < margin) y = margin
		}

		setCardPos({ x, y })
	}

	useEffect(() => {
		function handlePointerDown(event: PointerEvent) {
			if (!activeCard) return

			const target = event.target as Node | null
			if (!target) return

			if (cardRef.current && cardRef.current.contains(target)) return

			const maybeMarker = target instanceof Element && target.closest('[data-marker-id]')
			if (maybeMarker) return

			setActiveCard(null)
		}

		document.addEventListener('pointerdown', handlePointerDown)
		return () => document.removeEventListener('pointerdown', handlePointerDown)
	}, [activeCard])

	if (locations.length === 0) {
		return null
	}

	const activeLocation = locations.find((location) => location.id === activeCard)

	return (
		<section className="my-16">
			<div className="container">

				<div className="flex flex-col items-start gap-3 max-w-175">
					{overline && <p className="body text-black-950">{overline}</p>}
					{title && <h2 className="h2 text-black-950">{title}</h2>}
					{intro && <p className="body text-black-950">{intro}</p>}
					{(primaryCta || secondaryCta) && (
						<div className="flex items-center gap-4 mt-3">
							{primaryCta && (
								<Button variant="primary" href={primaryCta.url} isExternal={primaryCta.isExternal}>
									{primaryCta.text}
								</Button>
							)}
							{secondaryCta && (
								<Button variant="secondary" href={secondaryCta.url} isExternal={secondaryCta.isExternal}>
									{secondaryCta.text}
								</Button>
							)}
						</div>
					)}
				</div>

				<div
					ref={mapRef}
					className="relative mt-8 w-full overflow-hidden rounded-lg bg-brand-100
						aspect-400/489
						sm:aspect-auto sm:h-[30.5625rem]
						md:h-[26.875rem]
						lg:aspect-1234/520 lg:h-auto"
				>

					<div className="absolute top-4 left-4 z-10 inline-flex items-center overflow-hidden rounded-full border border-black-950 bg-pure-white">
						<button
							type="button"
							aria-label={zoomInLabel}
							disabled={isZoomMax}
							onClick={handleZoomIn}
							className="flex size-11 items-center justify-center text-black-950 disabled:opacity-40 disabled:pointer-events-none"
						>
							<Plus className="size-5" aria-hidden="true" />
						</button>
						<button
							type="button"
							aria-label={zoomOutLabel}
							disabled={isZoomMin}
							onClick={handleZoomOut}
							className="flex size-11 items-center justify-center border-l border-black-950 text-black-950 disabled:opacity-40 disabled:pointer-events-none"
						>
							<Minus className="size-5" aria-hidden="true" />
						</button>
					</div>

					<ComposableMap
						projection="geoMercator"
						projectionConfig={{ scale }}
						width={mapSize.width}
						height={mapSize.height}
						className="block w-full h-full"
					>
						<ZoomableGroup
							key={`${Math.round(mapSize.width)}x${Math.round(mapSize.height)}`}
							center={center}
							zoom={zoom}
							minZoom={1}
							maxZoom={20}
							translateExtent={translateExtent}
							filterZoomEvent={(event) => (event as unknown as Event).type !== 'wheel'}
							onMoveEnd={(position) => {
								if (position?.coordinates) setCenter(position.coordinates)
								if (position?.zoom) setZoom(position.zoom)
							}}
						>
							<Geographies geography={GEO_URL}>
								{({ geographies }) =>
									geographies.map((geo) => (
										<Geography
											key={geo.rsmKey}
											geography={geo}
											strokeWidth={0.5}
											className="fill-brand-600 stroke-brand-100 outline-none"
										/>
									))
								}
							</Geographies>

							{locations.map(({ id, name, coordinates }) => {
								const screenY = mapSize.height / 2 + (projection(coordinates)![1] - centerY) * zoom
								const isNearTop = screenY < LABEL_HEIGHT * zoom
								const labelY = isNearTop ? 14 : -14
								const textY = isNearTop ? 18 : -10

								return (
									<Marker key={id} coordinates={coordinates}>
										<g data-marker-id={id} className="cursor-pointer" onClick={(e) => handleMarkerClick(e, id)}>
											<circle r={4} className="fill-brand-600" />
											<g transform="translate(-6, -6)">
												<path
													className="fill-black-950"
													d="M6 0C7.59102 7.87902e-05 9.11711 0.631863 10.2422 1.75684C11.3673 2.882 11.999 4.40878 11.999 6C11.9988 9.74447 7.84509 13.6442 6.4502 14.8486C6.32036 14.9462 6.1624 14.999 6 14.999C5.83741 14.999 5.67878 14.9463 5.54883 14.8486C4.15391 13.6442 0.000220041 9.74445 0 6C0 4.40878 0.631673 2.882 1.75684 1.75684C2.882 0.631673 4.40878 0 6 0ZM6 3.75C4.75742 3.75 3.75 4.75742 3.75 6C3.75014 7.24246 4.75751 8.25 6 8.25C7.24237 8.24986 8.24986 7.24237 8.25 6C8.25 4.75751 7.24246 3.75014 6 3.75Z"
												/>
											</g>
											<g>
												{(() => {
													const charWidth = 6
													const rectWidth = Math.max(34, name.length * charWidth)
													const rectX = -rectWidth / 2

													return (
														<>
															<rect
																x={rectX}
																y={labelY}
																width={rectWidth}
																height={1}
																strokeWidth={12}
																strokeLinejoin="round"
																className="fill-black-950 stroke-black-950"
															/>
															<text textAnchor="middle" y={textY} className="fill-pure-white text-[0.625rem]">
																{name}
															</text>
														</>
													)
												})()}
											</g>
										</g>
									</Marker>
								)
							})}
						</ZoomableGroup>
					</ComposableMap>

					{activeLocation && (
						<div
							ref={cardRef}
							key={activeLocation.id}
							className="absolute z-20 w-75 max-w-[calc(100%-1.25rem)] max-h-[calc(100%-1.25rem)] rounded-lg bg-black-950 p-4"
							style={{ top: cardPos.y, left: cardPos.x }}
						>
							<div className="flex items-start justify-between gap-3">
								<p className="h5 text-pure-white">{activeLocation.name}</p>
								<button
									type="button"
									aria-label={closeLabel}
									onClick={() => setActiveCard(null)}
									className="flex size-8 shrink-0 items-center justify-center rounded-full bg-pure-white text-black-950"
								>
									<X className="size-4" aria-hidden="true" />
								</button>
							</div>
							{activeLocation.address && (
								<p className="body-small text-pure-white/72 mt-2">{activeLocation.address}</p>
							)}
							{activeLocation.email && (
								<p className="body-small mt-2">
									<a href={`mailto:${activeLocation.email}`} className="text-brand-500 hover:text-pure-white">
										{activeLocation.email}
									</a>
								</p>
							)}
						</div>
					)}

				</div>
			</div>
		</section>
	)
}
